import { ContentLoader } from "@rio-cloud/rio-uikit";
import { VehicleSupport } from "../../../types";
import moment from 'moment';
import { useIntl } from 'react-intl';

type VehicleDataItemProps = {
    title: string;
    value?: string | number | boolean;
    isLoading: boolean;
};

interface Props {
    data: VehicleSupport | null;
    isLoading: boolean;
}

const VehicleDataItem = ({ title, value, isLoading }: VehicleDataItemProps) => {
    return (
        <div className="flex items-start justify-between gap-4">
            <span className="text-color-dark text-size-14">{title}</span>

            {isLoading ? (
                <div style={{ width: 80, height: 20, borderRadius: 0, overflow: 'hidden' }}>
                    <ContentLoader className="width-100pct height-100pct" />
                </div>
            ) : (
                <span className="text-color-darker text-size-16 text-right">
                    {value || '-'}
                </span>
            )}
        </div>
    );
};

const formatDate = (date?: string, format = "DD/MM/YYYY") => {
    if (!date) {
        return '-'
    };

    return moment(date).format(format)
}

const formatBooleanValue = (value?: boolean | null): string => {
  if (typeof value !== 'boolean') {
    return '-';
  }

  return value ? 'Sim' : 'Não';
};

const VehicleData = ({ data, isLoading }: Props) => {
    const intl = useIntl();
    const vehicleData = [
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.vehicleOwner' }),
            value: data?.vehicleOwner,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.model' }),
            value: data?.model,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.modelCode' }),
            value: data?.modelCode,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.modelYear' }),
            value: data?.modelYear,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:tas.occurrence.chassis' }),
            value: data?.chassis,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:tas.occurrence.licensePlate' }),
            value: data?.licensePlate,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.colorDescription' }),
            value: data?.color,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.implementType' }),
            value: data?.implementType,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.productionDate' }),
            value: formatDate(data?.productionDate),
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.billingDate' }),
            value: formatDate(data?.billingDate),
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.tsDeliveryDate' }),
            value: formatDate(data?.tsDeliveryDate),
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.responsibleDealer' }),
            value: data?.responsibleDealer,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.warrantyEnd' }),
            value: formatDate(data?.warrantyEnd),
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.contractNumber' }),
            value: data?.contractNumber,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.contractModality' }),
            value: data?.contractModality,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.vehicleStatus' }),
            value: data?.vehicleStatus,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.contractStart' }),
            value: formatDate(data?.contractStart),
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.contractEnd' }),
            value: formatDate(data?.contractEnd),
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.kmLimit' }),
            value: `${data?.kmLimit || '-'}`,
        },
        {
            title: intl.formatMessage({ id: 'intl-msg:support.vehiclesData.pendingCampaigns' }),
            value: data?.campaigns,
        },
    ];

    return (
        <div className="border rounded width-100pct padding-15">
            <div className="margin-bottom-15">
                <span className="text-size-16 text-medium ">
                    {intl.formatMessage({ id: 'intl-msg:support.vehiclesData.title' })}
                </span>
            </div>

            <div className="display-flex flex-column">
                {vehicleData.map(item => (
                    <VehicleDataItem
                        key={item.title}
                        title={item.title}
                        value={item.value}
                        isLoading={isLoading}
                    />
                ))}
            </div>
        </div>
    );
};

export default VehicleData;